import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type {
  MachineCatalogApplySuggestionsPayload,
  MachineCatalogEntry,
  MachineDatasheet,
} from '@indutrust/machine-model';
import { EnvironmentService } from '@indutrust/shared-env';
import { BaseApiClient } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';

/**
 * API client for the shared machine catalog — `/machine_catalog`.
 *
 * Datasheet upload/delete and admin extraction workflows use the dedicated
 * methods below; inherited JSON CRUD methods do not support multipart bodies.
 */
@Service()
export class MachineCatalogApiClient extends BaseApiClient<MachineCatalogEntry> {
  protected envService = inject(EnvironmentService);
  protected http = inject(HttpClient);

  protected template: Partial<MachineCatalogEntry> = {};

  /**
   * Enqueues an async extraction job from the entry's uploaded datasheets.
   *
   * @example
   * client.analyzeDatasheets(7).subscribe();
   */
  analyzeDatasheets(id: number): Observable<MachineCatalogEntry> {
    return this.http.post<MachineCatalogEntry>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/analyze_datasheets`,
      {}
    );
  }

  /**
   * Merges accepted suggestion ids into the entry and clears pending suggestions.
   *
   * @example
   * client.applySuggestions(7, { capability_ids: [11] }).subscribe();
   */
  applySuggestions(
    id: number,
    payload: MachineCatalogApplySuggestionsPayload
  ): Observable<MachineCatalogEntry> {
    return this.http.post<MachineCatalogEntry>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/apply_suggestions`,
      payload
    );
  }

  /**
   * Approves a pending catalog entry so suppliers can select it.
   *
   * @example
   * client.approve(7).subscribe();
   */
  approve(id: number): Observable<MachineCatalogEntry> {
    return this.http.patch<MachineCatalogEntry>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/approve`,
      {}
    );
  }

  /**
   * Deletes a specific datasheet from a catalog entry.
   *
   * @example
   * client.deleteDatasheet(7, 12).subscribe();
   */
  deleteDatasheet(id: number, datasheetId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/datasheets/${datasheetId}`
    );
  }

  /**
   * Clears pending extraction suggestions without merging them.
   *
   * @example
   * client.dismissSuggestions(7).subscribe();
   */
  dismissSuggestions(id: number): Observable<MachineCatalogEntry> {
    return this.http.post<MachineCatalogEntry>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/dismiss_suggestions`,
      {}
    );
  }

  /**
   * Rejects a pending catalog entry with an optional reason.
   *
   * @example
   * client.reject(7, 'Duplicate of entry 3').subscribe();
   */
  reject(id: number, reason = ''): Observable<MachineCatalogEntry> {
    return this.http.patch<MachineCatalogEntry>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/reject`,
      { reason }
    );
  }

  /**
   * Uploads a datasheet file for a catalog entry using `multipart/form-data`.
   * Does not start extraction — call {@link analyzeDatasheets} after upload.
   *
   * @example
   * client.uploadDatasheet(7, file).subscribe();
   */
  uploadDatasheet(id: number, file: File): Observable<MachineDatasheet> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('name', file.name);
    return this.http.post<MachineDatasheet>(
      `${this.envService.getApiUrl()}/machine_catalog/${id}/datasheets`,
      form
    );
  }

  get url(): string {
    return `${this.envService.getApiUrl()}/machine_catalog`;
  }
}
